import { MjmlColumn, MjmlSection, MjmlText } from "@faire/mjml-react";
import {
  Alignment,
  ButtonLinkType,
  Section,
  SharedProps,
  Size,
  getButtonSize,
} from "@redotech/redo-model/email-builder";
import { memo } from "react";

export type ButtonState = Section.Button & SharedProps;

/**
 * An email button block (i.e., an entire section to itself)
 */
export const EmailButton = memo(function EmailButton(
  state: ButtonState & { noPadding?: boolean; autoWidth?: boolean },
) {
  return (
    <MjmlSection>
      <MjmlColumn>
        <MjmlText>
          <InlineEmailButton {...state} />
        </MjmlText>
      </MjmlColumn>
    </MjmlSection>
  );
});

export const NestedEmailButton = memo(function NestedEmailButton(
  state: ButtonState & {
    noPadding?: boolean;
    autoWidth?: boolean;
    bypassUtm?: boolean;
  },
) {
  return (
    <MjmlText>
      <InlineEmailButton {...state} />
    </MjmlText>
  );
});

/** A button that can be placed inside of other email sections.
 *
 * @remarks
 *  An MjmlButton component exists, but it only renders <a> tags, not <button> tags,
 *  so AMP emails won't work. The long term solution would be to treat <a> and <button> as totally separate components,
 *  but for now, MjmlRaw.
 */
export const InlineEmailButton = memo(function EmailButton(
  state: ButtonState & {
    noPadding?: boolean;
    autoWidth?: boolean;
    bypassUtm?: boolean;
  },
) {
  const buttonSize = getButtonSize(state.size, state.width, state.height);
  const padding = state.noPadding
    ? { top: 0, right: 0, bottom: 0, left: 0 }
    : (state.sectionPadding ?? { top: 0, right: 0, bottom: 0, left: 0 }); // this coalesce is necessary because lineItemButtons and checkoutButton haven't been migrated properly

  const dynamicButtonLink = state.schemaFieldName
    ? (state.schemaInstance[state.schemaFieldName] as string | undefined)
    : undefined;
  const originalButtonLink = state.linkType
    ? state.linkType === ButtonLinkType.WEB_PAGE
      ? state.buttonLink
      : dynamicButtonLink
    : state.buttonLink || dynamicButtonLink; //backwards compatibility

  const buttonLink = originalButtonLink
    ? state.bypassUtm
      ? originalButtonLink
      : state.utm.applyToUrl(originalButtonLink)
    : undefined; // this will happen on amp emails as buttons are true buttons not hyperlinks

  // i.e., don't show subscribe link if already subscribed
  if (!state.isBuilder && !buttonLink && state.as === "a") {
    return null;
  }

  const sizeToPadding: Map<Size, number> = new Map([
    [Size.MEDIUM, 12],
    [Size.SMALL, 8],
    [Size.LARGE, 16],
  ]);

  const As = state.as || "a";
  return (
    <div style={{ width: "100%", backgroundColor: state.sectionColor }}>
      <table
        style={{
          textAlign: state.alignment,
          width: "100%",
          margin: state.alignment === Alignment.CENTER ? "0 auto" : undefined,
          backgroundColor: state.sectionColor,
        }}
      >
        <tbody>
          <tr>
            <td
              style={{
                padding: `${padding.top}px ${padding.right}px ${padding.bottom}px ${padding.left}px`,
              }}
            >
              <As
                href={state.isBuilder || As !== "a" ? undefined : buttonLink}
                style={{
                  display: "inline-block",
                  fontWeight: "bold",
                  minWidth: state.autoWidth
                    ? undefined
                    : `min(${buttonSize.width}px, 100%)`,
                  width: state.fullWidth ? "100%" : undefined,
                  padding: `${sizeToPadding.get(state.size)}px 16px`, // TODO eventually make a setting for padding instead of the size selector
                  backgroundColor: state.fillColor,
                  color: state.textColor,
                  borderRadius: `${state.cornerRadius}px`,
                  border: `${state.strokeWeight}px solid ${state.strokeColor}`,
                  fontFamily: state.fontFamily,
                  fontSize: `${state.fontSize}px`,
                  textAlign: "center",
                  textDecoration: "none",
                  boxSizing: "border-box",
                  cursor: "pointer",
                }}
                type={state.buttonType}
              >
                {state.buttonText}
              </As>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
});
